/* eslint-disable max-statements */
/* eslint-disable max-len */
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContextData } from '../features/AppContext';
import { handleCookies, handleRedirect } from './../handleCookies/index';
import { APP_NAME_STUDIO, APP_NAME_USER, FIRST_INDEX, KEYCLOAK_TYPE, NEGATIVE_ONE, OAUTH_TYPE, prepareLabURL } from '../utilities/constants';
import { sanitizeInput } from '../utilities/sanitizer';
import { triggerSecQuesCall } from '../utilities/securityQuestionAPI';
import { handleApi } from '../utilities/api';
import { getOrgDetails, getOrgDetailsForThreePartURL } from '../utilities/getOrgEnvDetails';
import { getLocalString } from '../languages';


export const useLoginApi = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useContext(AppContextData);
  const isAccessAllowedOnlyToStudioApp = (data) => {
    return data?.isDashboardEditor;
  };

  const setCookies = (data) => {
    handleCookies(data, state.env_id, state.env_lab, state.authTypeResponse);
    handleRedirect(data, { ...state, app_name: isAccessAllowedOnlyToStudioApp(data) ? APP_NAME_STUDIO: APP_NAME_USER });
  };

  const handleAuthTypeApi = async (stateData) => {
    try {
      // Since this API gets called first even before state params get set,
      // a simple check is needed else the lab will always be from env typed in env field
      let envLab = stateData.env_lab;
      if(!envLab) {
        const { isEnvLabInUrl, envLabFromParams } = getEnvLabPresentInUrl();
        envLab = isEnvLabInUrl ? envLabFromParams: getLabEndPoint(stateData);
        dispatch({
          type: 'ENV_LAB',
          payload: envLab
        });
      }
      const { data: orgData } = await getOrgDetailsForThreePartURL(stateData.org_id, stateData.env_id);
      const { data } = await handleAuthType({ ...stateData, org_id: orgData?.orgId }, envLab);

      if(data.status) {
        let provider = data.data[FIRST_INDEX].provider;
        if(provider && provider.endsWith('/')) {
          provider = provider.slice(FIRST_INDEX, NEGATIVE_ONE);
        }
        const authTypeData = { ...data.data[FIRST_INDEX], provider };

        // eslint-disable-next-line no-console
        console.info('[OAuth] Auth type received:', {
          authType: authTypeData.authType,
          provider: authTypeData.provider,
          clientId: authTypeData.clientId ? '✓ present' : '✗ missing',
          redirectUri: authTypeData.redirectUri ? '✓ present' : '✗ missing',
          orgId: authTypeData.orgId
        });

        dispatch({
          type: 'AUTH_TYPE_SUCCESS',
          payload: authTypeData
        });

        // React 18 fix: Pass authTypeData through navigation state to avoid timing issues
        // Context state might not update before component renders after navigation
        if(data.data[FIRST_INDEX] && data.data[FIRST_INDEX].authType === KEYCLOAK_TYPE) {
          navigate('/keycloak-sign-in', {
            replace: true,
            state: { authTypeResponse: authTypeData }
          });
        } else if(data.data[FIRST_INDEX] && data.data[FIRST_INDEX].authType !== OAUTH_TYPE) {
          // eslint-disable-next-line no-console
          console.info('[OAuth] Non-OAuth auth type, navigating to /sign-in');
          navigate('/sign-in', { replace: true });
        }else if(data.data[FIRST_INDEX]?.authType === OAUTH_TYPE) {
          // eslint-disable-next-line no-console
          console.info('[OAuth] OAuth auth type, navigating to /auth-sign-in with state');
          navigate('/auth-sign-in', {
            replace: true,
            state: { authTypeResponse: authTypeData }
          });
        }
      }
    } catch (e) {
      dispatch({
        type: 'SET_LOADER',
        payload: false
      });
      handleServerError(e);
    }
  };

  const handleServerError = (e) => {
    const status = e?.response?.status ?? undefined;
    const errorZRequestId = e?.response?.data?.zRequestId ?? undefined;
    if ((status >= 500 && !errorZRequestId) || !status) {
      dispatch({
        type: 'SET_ERROR',
        payload: getLocalString('api_server_error', 'Server Error')
      });
    }
  };

  const handleLoginApi = async (stateData) => {
    try{
      const { data: loginData } = await handleSignIn({ ...stateData, org_id: stateData?.authTypeResponse?.orgId || stateData?.org_id });
      const data = { ...loginData, orgIdAlias: stateData?.authTypeResponse?.orgAlias };

      dispatch({
        type: 'LOGIN_SUCCESS'
      });
      if(data.status) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: data
        });
        if(isAccessAllowedOnlyToStudioApp(data)) {
          dispatch({
            type: 'APP_NAME',
            payload: APP_NAME_STUDIO
          });
        }
        if(data.istwoFactorAuthEnabled === true) {
          window.location.pathname = '/TwoFactorAuth';
        }else if (data.isSecurityQuestionNeedToBeAnswered) {
          await triggerSecQuesCall(data, state, false);
        }else {
          setCookies(data);
        }
      }
    }catch (e) {
      dispatch({
        type: 'SET_LOADER',
        payload: false
      });
      const message = (() => {
        try {
          return e.response.data['errorMessage'];
        } catch (e) {
          return 'Something went wrong. Please try again';
        }
      })();
      if (e?.response?.data?.errorCode === 'SES5045') {
        navigate('/forgot-password', { replace: true, state: { message, email: stateData.login_id } });
      }
      const status = e?.response?.status ?? undefined;
      if(status >= 500 || !status) {
        handleServerError(e);
      }else {
        dispatch({
          type: 'SET_ERROR',
          payload: message
        });
      }
    }
  };

  const handleAuthLoginApi = async (stateData, idToken, auth0FromHook, accessToken) => {
    try {
      const { data: loginData } = await handleAuthSignIn(
        {
          ...stateData,
          org_id: stateData?.authTypeResponse?.orgId || stateData?.org_id
        },
        idToken,
        accessToken
      );
      const data = {
        ...loginData,
        orgIdAlias: stateData?.authTypeResponse?.orgAlias
      };

      if (data.status) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: data
        });
        if (data.istwoFactorAuthEnabled === true) {
          window.location.pathname = '/TwoFactorAuth';
        } else {
          setCookies(data);
        }
      }
    } catch (e) {
      dispatch({
        type: 'SET_LOADER',
        payload: false
      });
      const message = (() => {
        try {
          return e.response.data['errorMessage'];
        } catch (e) {
          return 'Something went wrong. Please try again';
        }
      })();
      dispatch({
        type: 'SET_ERROR',
        payload: message
      });
      if (message && e.response.data['errorCode']) {
        const errorCode = encodeURIComponent(e.response.data['errorCode']);
        const errorMessage = encodeURIComponent(message);
        await auth0FromHook.logout({
          returnTo: window.location.origin + `/error?code=${errorCode}&message=${errorMessage}`
        });
      } else {
        const webLogoutURL = e.response?.data?.['webLogOutUrl'];
        if (webLogoutURL) {
          const returnTo = `${webLogoutURL}?post_logout_redirect_uri=https://${window.location.host}`;
          window.location.href = `https://${stateData.authTypeResponse?.provider}/v2/logout/?client_id=${stateData.authTypeResponse?.clientId}&returnTo=${returnTo}`;
        } else {
          window.location.href = `https://${stateData.authTypeResponse?.provider}/v2/logout/?federated&client_id=${stateData.authTypeResponse?.clientId}&returnTo=https://${window.location.host}`;
        }
      }
    }
  };

  const handleKeycloakLoginApi = async (stateData, keycloakToken, loginId) => {
    try {
      const orgId = stateData?.authTypeResponse?.orgId || stateData?.org_id;
      const envLab = getLabEndPoint(stateData);
      const { data: contextData } = await handleApi(
        `${envLab}/keycloak/userContext`,
        {
          token: `Bearer ${keycloakToken}`,
          loginId: loginId,
          orgId: orgId,
          businessUnit: orgId
        }
      );
      const kcData = contextData?.data || contextData;
      const data = {
        ...kcData,
        token: keycloakToken,
        orgIdAlias: kcData?.orgIdAlias || stateData?.authTypeResponse?.orgAlias || orgId
      };
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: data
      });
      if (data.istwoFactorAuthEnabled === true) {
        window.location.pathname = '/TwoFactorAuth';
      } else if (data.isSecurityQuestionNeedToBeAnswered) {
        await triggerSecQuesCall(data, state, false);
      } else {
        setCookies(data);
      }
    } catch (e) {
      dispatch({ type: 'SET_LOADER', payload: false });
      const message = (() => {
        try {
          return e.response.data['errorMessage'];
        } catch (_) {
          return 'Keycloak authentication failed. Please try again.';
        }
      })();
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  };

  const getLabEndPoint = ({ env_id, env_lab }) => prepareLabURL(env_id, env_lab);

  const getEnvLabPresentInUrl = () => {
    const queryString = window.location.search;
    let isEnvLabInUrl = false;
    let envLabFromParams;
    if (queryString) {
      const params = new URLSearchParams(queryString);
      envLabFromParams = process.env.REACT_APP_BACKEND_SERVER_URL ? process.env.REACT_APP_BACKEND_SERVER_URL : params.get('lab');
      if(envLabFromParams) {
        isEnvLabInUrl = true;
        return { isEnvLabInUrl, envLabFromParams };
      }
    }
    return { isEnvLabInUrl, envLabFromParams };
  };

  const handleSignIn = async (stateData) => {
    const endPoint ='session/login';
    const payload = getPayloadForSignInApi(stateData);
    const envLab = getLabEndPoint(stateData);
    const apiEndPoint = `${envLab}/${endPoint}`;
    return handleApi(apiEndPoint, payload);
  };

  const handleAuthSignIn = async (stateData, idToken, accessToken) => {
    const endPoint ='auth/validateAuthResponse';
    const payload = getPayloadForAuthApi(stateData, idToken, accessToken);
    const envLab = getLabEndPoint(stateData);
    const apiEndPoint = `${envLab}/${endPoint}`;
    return handleApi(apiEndPoint, payload);
  };

  const handleAuthType = async (stateData, envLab) => {
    const endPoint ='auth/getAuthType';
    const payload = getPayloadForAuthTypeApi(stateData);
    const apiEndPoint = `${envLab}/${endPoint}`;
    return handleApi(apiEndPoint, payload);
  };

  const fetchOrgAndOrgAlias = async (orgId, env) => {
    try {
      const orgDetailsRes = await getOrgDetails(orgId, env);
      const envUrl = orgDetailsRes?.data?.envUrl;
      const authTypeRes = await handleAuthType({ org_id: orgId }, envUrl);
      const orgIdAlias = authTypeRes?.data?.data?.[FIRST_INDEX]?.orgAlias || orgId;
      const origOrgId = authTypeRes?.data?.data?.[FIRST_INDEX]?.orgId || orgId;
      return { orgIdAlias: orgIdAlias, orgId: origOrgId };
    } catch (e) {
      return { orgIdAlias: orgId, orgId: orgId };
    }
  };

  const getPayloadForAuthTypeApi = (stateData) => {
    const auth = stateData;
    return {
      target: 'web',
      orgId: auth.org_id,
      'Content-Type': 'application/json'
    };
  };

  const getPayloadForSignInApi = (stateData) => {
    const signInData = stateData;
    return {
      loginId: signInData.login_id,
      password: signInData.password,
      orgId: signInData.org_id,
      target: 'web',
      'Content-Type': 'application/json'
    };
  };

  const getPayloadForAuthApi = (stateData, token, accessToken) => {
    return {
      'Content-Type': 'application/json',
      'orgId': sanitizeInput(stateData.org_id),
      // eslint-disable-next-line no-underscore-dangle
      'token': token.__raw,
      'accesstoken': accessToken,
      target: 'web'
    };
  };

  return {
    handleAuthTypeApi,
    handleLoginApi,
    handleAuthLoginApi,
    handleKeycloakLoginApi,
    handleAuthType,
    fetchOrgAndOrgAlias
  };
};
